import { Component, ElementRef, OnInit, ViewChild } from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";
import { NgbModal } from "@ng-bootstrap/ng-bootstrap";
import { DataServiceService } from "../data-service.service";
import { DateHelperServiceService } from "../helpers/date-helper-service.service";
import { User } from "../model/login";

@Component({
  selector: "app-view-user",
  templateUrl: "./view-user.component.html",
  styleUrls: ["./view-user.component.scss"],
})
export class ViewUserComponent implements OnInit {
  @ViewChild("batchScanFileInput")
  batchScanFileInput?: ElementRef<HTMLInputElement>;

  userData: User;
  isLoading: boolean = true;
  userId: string = "";
  batchScanReports: any[] = [];
  selectedBatchScanFile: File | null = null;
  batchScanTitle = "Search Report – Batch Scan";
  isUploadingBatchScan = false;
  isLoadingBatchScanReports = false;
  isBatchScanDragOver = false;
  deletingBatchScanReportId = "";
  batchScanUploadMessage = "";
  batchScanUploadError = "";
  constructor(
    public dateHelper: DateHelperServiceService,
    private dataService: DataServiceService,
    private router: Router,
    private route: ActivatedRoute
  ) {
    this.userId = this.route.snapshot.params.userId
      ? this.route.snapshot.params.userId
      : "";
  }

  ngOnInit(): void {
    this.getUserData().then(() => {
      this.isLoading = false;
      this.loadBatchScanReports();
    });
  }

  invalidForm() {
    return false;
  }

  editView(userId) {
    this.router.navigate(["/edit-user", userId]);
  }

  isAdverseMediaEnabled(user: User) {
    return user && user.isAdverseMediaEnabled === true;
  }

  onAdverseMediaChange(enabled: boolean) {
    const previous = this.isAdverseMediaEnabled(this.userData);
    this.userData.isAdverseMediaEnabled = enabled;
    this.dataService
      .updateAdverseMediaEnabled(this.userId, enabled)
      .catch(() => {
        this.userData.isAdverseMediaEnabled = previous;
        alert("Failed to update Adversary Media Search setting.");
      });
  }

  async getUserData() {
    this.userData = await this.dataService.getUserById(this.userId);
    this.userData = this.userData[0];
  }

  private readonly allowedBatchScanExtensions = [
    ".pdf",
    ".doc",
    ".docx",
    ".xls",
    ".xlsx",
    ".csv",
    ".txt",
    ".png",
    ".jpg",
    ".jpeg",
  ];

  triggerBatchScanFileInput() {
    if (!this.selectedBatchScanFile) {
      this.batchScanFileInput?.nativeElement.click();
    }
  }

  onBatchScanDragOver(event: DragEvent) {
    event.preventDefault();
    event.stopPropagation();
    this.isBatchScanDragOver = true;
  }

  onBatchScanDragLeave(event: DragEvent) {
    event.preventDefault();
    event.stopPropagation();
    this.isBatchScanDragOver = false;
  }

  onBatchScanDrop(event: DragEvent) {
    event.preventDefault();
    event.stopPropagation();
    this.isBatchScanDragOver = false;

    const file =
      event.dataTransfer?.files && event.dataTransfer.files.length > 0
        ? event.dataTransfer.files[0]
        : null;
    this.setBatchScanFile(file);
  }

  onBatchScanFileSelected(event: Event) {
    const input = event.target as HTMLInputElement;
    const file =
      input.files && input.files.length > 0 ? input.files[0] : null;
    this.setBatchScanFile(file);
  }

  clearBatchScanFile() {
    this.selectedBatchScanFile = null;
    this.batchScanUploadError = "";
    if (this.batchScanFileInput?.nativeElement) {
      this.batchScanFileInput.nativeElement.value = "";
    }
  }

  formatBatchScanFileSize(bytes: number) {
    if (!bytes) {
      return "0 B";
    }
    const units = ["B", "KB", "MB", "GB"];
    const index = Math.min(
      Math.floor(Math.log(bytes) / Math.log(1024)),
      units.length - 1,
    );
    const size = bytes / Math.pow(1024, index);
    return `${size.toFixed(index === 0 ? 0 : 1)} ${units[index]}`;
  }

  private setBatchScanFile(file: File | null) {
    this.batchScanUploadError = "";

    if (!file) {
      this.selectedBatchScanFile = null;
      return;
    }

    const ext = file.name.includes(".")
      ? file.name.slice(file.name.lastIndexOf(".")).toLowerCase()
      : "";
    if (!this.allowedBatchScanExtensions.includes(ext)) {
      this.selectedBatchScanFile = null;
      if (this.batchScanFileInput?.nativeElement) {
        this.batchScanFileInput.nativeElement.value = "";
      }
      this.batchScanUploadError =
        "Unsupported file type. Please upload PDF, Word, Excel, CSV, TXT, PNG, or JPG.";
      return;
    }

    this.selectedBatchScanFile = file;
  }

  async loadBatchScanReports() {
    if (!this.userId) {
      return;
    }
    this.isLoadingBatchScanReports = true;
    try {
      const response = await this.dataService.getBatchScanReportsByUserId(
        this.userId,
      );
      this.batchScanReports = (response && response.reports) || [];
    } catch (error) {
      this.batchScanReports = [];
    } finally {
      this.isLoadingBatchScanReports = false;
    }
  }

  async uploadBatchScanReport() {
    if (!this.userData || !this.selectedBatchScanFile) {
      this.batchScanUploadError = "Please select a supported file to upload.";
      return;
    }

    this.isUploadingBatchScan = true;
    this.batchScanUploadMessage = "";
    this.batchScanUploadError = "";

    try {
      await this.dataService.uploadBatchScanReport(
        this.userData.userId,
        this.userData.email,
        this.selectedBatchScanFile,
        this.batchScanTitle,
      );
      this.batchScanUploadMessage = "Batch scan report uploaded successfully.";
      this.clearBatchScanFile();
      this.batchScanTitle = "Search Report – Batch Scan";
      await this.loadBatchScanReports();
    } catch (error) {
      this.batchScanUploadError = "Failed to upload batch scan report.";
    } finally {
      this.isUploadingBatchScan = false;
    }
  }

  async viewBatchScanReport(report: any) {
    if (!report || !report.reportId) {
      return;
    }
    try {
      const response = await this.dataService.getBatchScanReportDownloadUrl(
        report.reportId,
      );
      if (response && response.url) {
        window.open(response.url, "_blank");
      }
    } catch (error) {
      alert("Failed to open batch scan report.");
    }
  }

  async deleteBatchScanReport(report: any) {
    if (!report || !report.reportId || this.deletingBatchScanReportId) {
      return;
    }

    const fileName = report.originalFileName || report.fileName || "this file";
    const confirmed = window.confirm(
      `Delete "${fileName}"? This cannot be undone.`,
    );
    if (!confirmed) {
      return;
    }

    this.deletingBatchScanReportId = report.reportId;
    this.batchScanUploadMessage = "";
    this.batchScanUploadError = "";

    try {
      await this.dataService.deleteBatchScanReport(report.reportId);
      this.batchScanReports = this.batchScanReports.filter(
        (item) => item.reportId !== report.reportId,
      );
      this.batchScanUploadMessage = "Batch scan report deleted successfully.";
    } catch (error) {
      this.batchScanUploadError = "Failed to delete batch scan report.";
    } finally {
      this.deletingBatchScanReportId = "";
    }
  }

  formatBatchScanDate(value: string) {
    if (!value) {
      return "-";
    }
    const date = new Date(value);
    return isNaN(date.getTime()) ? value : date.toLocaleString();
  }
}
